<script setup lang="ts">
/**
 * 起動時のローカルデータ移行提案 (§6-1/§6-4)。この端末の localStorage に
 * ユーザー作成物件が残っていて未移行 (migrations/{email} 未完了) の場合に、
 * Firestore への取り込みを提案する。自動移行はしない。
 *
 * 「今後表示しない」は docs=[] で runLocalImportV2 を呼び、migrations の完了
 * フラグだけを書く (bootstrap.ts の実装: 空配列なら writeBatch は flag の
 * merge のみで確定する)。「後で」は何も書かず閉じる — declined は非終端の
 * ため次回起動時に再提案される (§6-4)。
 */
import { ref, computed, watch } from 'vue';
import { isFirebaseConfigured } from '@/lib/firebase/init';
import { planLocalImportV2, runLocalImportV2 } from '@/lib/firebase/bootstrap';
import { useAuthStore } from '@/stores/authStore';
import type { PropertyV2 } from '@/types/property-v2';

const auth = useAuthStore();
const userDocs = ref<PropertyV2[]>([]);
const open = ref(false);
const busy = ref(false);
const importError = ref(false);

const displayedProperties = computed(() => userDocs.value.slice(0, 5));
const remainingCount = computed(() => Math.max(0, userDocs.value.length - 5));

/** 多重実行ガード: checking 中は再実行しない。processed は非 ready へ抜けた時点でリセットし、
 *  同一 ready 中の二重実行は防ぎつつ、次の ready 遷移 (再ログイン等) では必ず取り直す
 *  (古いスナップショット問題の解消)。
 */
let checking = false;
let processed = false;

// onMounted 一発だと、マウント時点で未サインインだった場合 (対話的サインイン後に
// ready へ遷移するケース) に本処理が一度も走らない。status の遷移を継続監視し、
// ready へ遷移するたびに (再ログイン等で) pending/userDocs を取り直す。
watch(
  () => auth.status,
  async (status) => {
    if (!isFirebaseConfigured()) return;
    if (status !== 'ready' || !auth.email) {
      processed = false;
      return;
    }
    if (checking || processed) return;
    checking = true;
    try {
      const plan = await planLocalImportV2(auth.email);
      processed = true;
      if (!plan.done && plan.userDocs.length > 0) {
        userDocs.value = plan.userDocs;
        open.value = true;
      }
    } finally {
      checking = false;
    }
  },
  { immediate: true },
);

async function doImport(): Promise<void> {
  busy.value = true;
  importError.value = false;
  try {
    // runLocalImportV2 には planLocalImportV2 が返した userDocs のみを渡す —
    // シード除外は既に plan 側で済んでいる契約 (§6-1)。
    await runLocalImportV2(auth.email!, userDocs.value, auth.displayName);
    // 取込結果 (取り込んだ物件・完了フラグ) を確実に一覧へ反映する最小手段
    // として reload する。ここでは busy を戻さない — 画面は再読込で消える。
    location.reload();
  } catch {
    importError.value = true;
    busy.value = false;
  }
}

async function dismiss(): Promise<void> {
  busy.value = true;
  importError.value = false;
  try {
    await runLocalImportV2(auth.email!, [], auth.displayName);
    open.value = false;
  } catch {
    importError.value = true;
  } finally {
    busy.value = false;
  }
}

function later(): void {
  open.value = false;
}
</script>

<template>
  <v-dialog
    v-model="open"
    max-width="480"
    persistent
  >
    <v-card>
      <v-card-title>物件データの取り込み</v-card-title>
      <v-card-text>
        この端末に保存されている物件 {{ userDocs.length }} 件を、チームのデータベースへ取り込みますか？
        (シード物件は対象外です)
        <ul>
          <li
            v-for="p in displayedProperties"
            :key="p.id"
          >
            {{ p.buildingName.ja }}
          </li>
        </ul>
        <div v-if="remainingCount > 0">
          ほか{{ remainingCount }}件
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="text"
          :disabled="busy"
          @click="later"
        >
          後で
        </v-btn>
        <v-btn
          variant="text"
          :disabled="busy"
          @click="dismiss"
        >
          今後表示しない
        </v-btn>
        <v-btn
          color="primary"
          :loading="busy"
          :disabled="busy"
          @click="doImport"
        >
          取り込む
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-snackbar
    v-model="importError"
    color="error"
    :timeout="6000"
  >
    取り込みに失敗しました (再試行できます)
  </v-snackbar>
</template>
